import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileLayout from '../../components/MobileLayout';
import { useAvatars } from '../../../hooks/useApi';
import './MobileAvatars.css';

const IconUser  = () => <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const IconPlus  = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const IconSearch = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;

export default function MobileAvatars() {
  const navigate = useNavigate();
  const [search, setSearch]       = useState('');
  const [page, setPage]           = useState(1);
  const [allAvatars, setAllAvatars] = useState([]);
  const sentinelRef = useRef(null);

  const { avatars, loading, error, totalPages, refetch, deleteAvatar } = useAvatars('my', page, search);

  // Accumulate pages: reset on page 1, append on subsequent pages
  useEffect(() => {
    if (page === 1) {
      setAllAvatars(avatars);
    } else {
      setAllAvatars((prev) => [...prev, ...avatars]);
    }
  }, [avatars]); // eslint-disable-line react-hooks/exhaustive-deps

  // IntersectionObserver: auto-load next page when sentinel scrolls into view
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loading && page < totalPages) {
          setPage((p) => p + 1);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loading, page, totalPages]);

  const handleSelect = (avatar) => {
    localStorage.setItem('selectedAvatar', JSON.stringify(avatar));
    navigate('/m/generate');
  };

  const handleDelete = async (avatar) => {
    if (!window.confirm(`确认删除「${avatar.name}」？`)) return;
    try {
      await deleteAvatar(avatar.key || avatar.id);
    } catch (err) {
      alert(err.message || '删除失败');
    }
  };

  const handleCreateNew = () => {
    navigate('/m/create-avatar', { state: { returnTo: '/m/avatars' } });
  };

  return (
    <MobileLayout title="我的形象">
      <div className="mav-root">
        {/* Search */}
        <div className="mav-search-wrap">
          <span className="mav-search-icon"><IconSearch /></span>
          <input
            className="mav-search"
            type="search"
            placeholder="搜索形象名称..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>

        {loading && page === 1 ? (
          <div className="mav-loading"><div className="mav-spinner" /></div>
        ) : error ? (
          <div className="mav-error">{error} <button onClick={() => refetch()}>重试</button></div>
        ) : (
          <>
            <div className="mav-grid">
              {/* Create new */}
              <div className="mav-create-card" onClick={handleCreateNew}>
                <div className="mav-create-icon"><IconPlus /></div>
                <div className="mav-create-label">创建新形象</div>
              </div>

              {allAvatars.map((avatar) => (
                <div key={avatar.id} className="mav-card" onClick={() => handleSelect(avatar)}>
                  <div className="mav-thumb">
                    {avatar.thumbnail
                      ? <img src={avatar.thumbnail} alt={avatar.name} loading="lazy" />
                      : <IconUser />
                    }
                  </div>
                  <div className="mav-card-body">
                    <div className="mav-card-name">{avatar.name}</div>
                    {avatar.createdAt && <div className="mav-card-date">{avatar.createdAt}</div>}
                    <div className="mav-card-actions">
                      <button className="mav-card-btn select" onClick={(e) => { e.stopPropagation(); handleSelect(avatar); }}>选择</button>
                      <button className="mav-card-btn delete" onClick={(e) => { e.stopPropagation(); handleDelete(avatar); }}>删除</button>
                    </div>
                  </div>
                </div>
              ))}

              {allAvatars.length === 0 && !loading && (
                <div className="mav-empty">
                  {search ? '没有匹配的形象' : '还没有数字人形象'}
                </div>
              )}
            </div>

            {/* Sentinel for IntersectionObserver */}
            <div ref={sentinelRef} className="mav-sentinel" />

            {/* Bottom loading indicator */}
            {loading && page > 1 && (
              <div className="mav-bottom-loading"><div className="mav-spinner" /></div>
            )}

            {/* All loaded hint */}
            {!loading && totalPages > 0 && page >= totalPages && allAvatars.length > 0 && (
              <div className="mav-all-loaded">已加载全部形象</div>
            )}
          </>
        )}
      </div>
    </MobileLayout>
  );
}
